import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MILEAGE_TIER, MILEAGE_TIER_THRESHOLDS, type MileageTier } from '@soroush/shared';
import { BrandLogo } from '../components/BrandLogo';
import { toFaDigits } from '../lib/jalali';

const TIERS = [
  MILEAGE_TIER.BLUE,
  MILEAGE_TIER.SILVER,
  MILEAGE_TIER.GOLD,
  MILEAGE_TIER.PLATINUM,
] as const;

const TIER_IMAGE: Record<MileageTier, string> = {
  BLUE: '/images/club/tier-blue.png',
  SILVER: '/images/club/tier-silver.png',
  GOLD: '/images/club/tier-gold.png',
  PLATINUM: '/images/club/tier-platinum.png',
};

const SERVICES = ['miles', 'baggage', 'lounge', 'priority'] as const;

export function ClubLanding() {
  const { t, i18n } = useTranslation();
  const isFa = i18n.language.startsWith('fa');

  const milesLabel = (value: number) => {
    const text = value.toLocaleString('en-US');
    return isFa ? toFaDigits(text) : text;
  };

  return (
    <main className="sa-page club-landing">
      <section className="club-hero" aria-labelledby="club-hero-title">
        <div className="club-hero__media" aria-hidden="true">
          <img src="/images/hero-home.jpg?v=13" alt="" width={1920} height={1080} fetchPriority="high" />
        </div>
        <div className="club-hero__veil" aria-hidden="true" />

        <div className="club-hero__sheet">
          <BrandLogo variant="wordmark" size="lg" to={null} className="club-hero__brand" priority />
          <p className="club-hero__eyebrow">{t('club.landing.heroEyebrow')}</p>
          <h1 id="club-hero-title" className="club-hero__title">
            {t('club.landing.heroTitle')}
          </h1>
          <p className="club-hero__lead">{t('club.landing.heroLead')}</p>
          <div className="club-hero__actions">
            <Link to="/login" className="sa-button sa-button--primary">
              {t('club.landing.joinCta')}
            </Link>
            <a href="#club-tiers" className="sa-button sa-button--ghost club-hero__ghost">
              {t('club.landing.seeBenefits')}
            </a>
          </div>
        </div>
      </section>

      <section className="club-welcome" aria-labelledby="club-welcome-title">
        <div className="sa-container club-welcome__inner">
          <h2 id="club-welcome-title">{t('club.landing.welcomeTitle')}</h2>
          <p>{t('club.landing.welcomeLead')}</p>
        </div>
      </section>

      <section id="club-tiers" className="club-tiers" aria-labelledby="club-tiers-title">
        <div className="sa-container club-tiers__grid">
          <div className="club-tiers__copy">
            <p className="home-section__eyebrow">{t('club.eyebrow')}</p>
            <h2 id="club-tiers-title">{t('club.landing.tiersTitle')}</h2>
            <p className="club-tiers__lead">{t('club.landing.tiersLead')}</p>
            <a href="#club-services" className="club-tiers__link">
              {t('club.landing.seeBenefits')}
            </a>
          </div>
          <div className="club-tiers__visual">
            <img
              src="/images/club/tiers-fan.png"
              alt={t('club.landing.tiersVisualAlt')}
              width={2016}
              height={1344}
              loading="lazy"
              decoding="async"
            />
          </div>
        </div>
      </section>

      <section className="club-tier-cards" aria-label={t('club.landing.tiersTitle')}>
        <div className="sa-container">
          <ul className="club-tier-cards__list">
            {TIERS.map((tier, index) => (
              <li
                key={tier}
                className={`club-tier-card club-tier-card--${tier.toLowerCase()}`}
                style={{ animationDelay: `${0.08 * index}s` }}
              >
                <div className="club-tier-card__face">
                  <img
                    src={TIER_IMAGE[tier]}
                    alt=""
                    width={505}
                    height={570}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="club-tier-card__body">
                  <h3>{t(`club.landing.tierNames.${tier}`)}</h3>
                  <p className="club-tier-card__miles">
                    {t('club.landing.fromMiles', { miles: milesLabel(MILEAGE_TIER_THRESHOLDS[tier]) })}
                  </p>
                  <p>{t(`club.benefits.${tier}`)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="club-services" className="club-services" aria-labelledby="club-services-title">
        <div className="sa-container">
          <div className="club-services__head">
            <h2 id="club-services-title">{t('club.landing.servicesTitle')}</h2>
          </div>
          <ul className="club-services__list">
            {SERVICES.map((key) => (
              <li key={key} className="club-service">
                <h3>{t(`club.landing.services.${key}.title`)}</h3>
                <p>{t(`club.landing.services.${key}.lead`)}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="club-final" aria-labelledby="club-final-title">
        <div className="sa-container club-final__inner">
          <BrandLogo variant="emblem" size="lg" to={null} />
          <h2 id="club-final-title">{t('club.landing.finalTitle')}</h2>
          <p>{t('club.landing.finalLead')}</p>
          <Link to="/login" className="sa-button sa-button--primary">
            {t('club.landing.joinCta')}
          </Link>
        </div>
      </section>
    </main>
  );
}
